import React, { useState, useEffect } from 'react';
import {
  IonIcon,
  IonImg,
} from '@ionic/react';
import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import AvatarRole from '../../components/AvatarRole';
import ResponseBox from './ResponseBox';
import './CatchupCard.css';
import PostIndicator from '../../components/PostIndicator';
import PdfIndicator from '../../common/atoms/pdfIndicator/PdfIndicator';
import VideoPlayer from '../../components/VideoPlayer';
import TruncateText from '../../components/TruncateText';
import { displayName } from '../../helpers/textManipulation';
import { getMediaType } from '../../helpers/comments';
import appStrings from '../../common/app_strings';
import appColors from '../../common/app_colors';
import { useAppState } from '../../providers/app-state/app-state-provider';

const CATCHUP_STRINGS = appStrings.catchup;
const { text } = appColors;

const STUDENT_ROLES = ['Student', 'Scholar', 'Foundations'];
const PROFESSIONAL_ROLES = ['Professional', 'Legacy Backr'];
const ADMIN_ROLES = ['Admin User'];

const styles = {
  mediaContainerStyle: {
    borderRadius: '10px',
    marginTop: '1rem',
  },
  affiliationNameStyle: {
    color: text.lightBlack,
    fontSize: '12px',
    fontWeight: 400,
    opacity: '0.76',
    marginTop: '-5px',
  },
  postAuthorNameStyle: {
    color: text.darkNavy,
    fontSize: '20px',
    fontWeight: 700,
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  imgStyle: {
    '--border-radius': '10px',
    maxWidth: '200px',
  },
  videoContainerStyle: {
    maxWidth: '300px',
  },
  avatarRoleStyles: {
    avatar: { width: '61px', height: '61px' },
    roleIndicator: { width: '22px', height: '22px' },
  },
  celebrationTextStyle: {
    fontSize: '16px',
    fontWeight: 700,
    color: text.lightBlack,
    marginTop: '-3rem',
  },
  plusIconStyle: {
    width: '37px',
    height: '37px',
  },
  authorStackStyle: {
    flex: 1,
    minWidth: 0,
  },
  editIconStyle: {
    width: '24px',
    minWidth: '24px',
    height: '24px',
    cursor: 'pointer',
  },
  pillContainerStyle: {
    flexShrink: 0,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    marginTop: '0px !important',
    paddingTop: '5px',
  },
};

const getAffiliationName = (post) => {
  if (!post?.author) return null;

  const { roles, current_school: currentSchool, current_employer: currentEmployer } = post.author;

  if (STUDENT_ROLES.some((role) => roles?.some((userRole) => userRole?.name === role))) {
    return currentSchool ?? null;
  }
  if (PROFESSIONAL_ROLES.some((role) => roles?.some((userRole) => userRole?.name === role))) {
    return currentEmployer ? currentEmployer.charAt(0).toUpperCase() + currentEmployer.slice(1) : null;
  }
  if (ADMIN_ROLES.some((role) => roles?.some((userRole) => userRole?.name === role))) {
    return 'Backrs';
  }
  return null;
};

const CatchupCard = ({
  post, onResponse, user, showCelebration, setShowCelebration, isResponding, setIsResponding,
}) => {
  const [media, setMedia] = useState(null);
  const [submittedResponse, setSubmittedResponse] = useState(null);
  const { platform } = useAppState();
  const postAttachment = post?.attachments[0] || null;
  const groupName = post?.groups?.[0]?.short_name || post?.groups?.[0]?.name;
  const panelName = post?.panel_owner?.first_name ? `${post.panel_owner.first_name}'s Panel` : null;

  const affiliationName = getAffiliationName(post) || null;

  const mediaType = getMediaType(postAttachment?.s3_key);
  const truncateLength = platform?.isNativePlatform ? ((mediaType && mediaType !== 'pdfs') ? 60 : 85) : 115;

  useEffect(() => {
    setIsResponding(false);
    setMedia(null);
    setSubmittedResponse(null);
  }, [post]);

  useEffect(() => {
    let observer;
    if (isResponding) {
      // Use a MutationObserver to watch for the textarea being added to the DOM
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.querySelector) {
              const textarea = node.querySelector('.comment-input');
              if (textarea) {
                textarea.setFocus();
                observer.disconnect();
              }
            }
          });
        });
      });

      // Start observing the document with the configured parameters
      observer.observe(document.body, { childList: true, subtree: true });
    }
    return () => observer?.disconnect();
  }, [isResponding]);

  const handleSubmit = async (response) => {
    const res = await onResponse(response);
    setSubmittedResponse({ ...res, media: response?.media });
    setIsResponding(false);

    // Scroll to bottom after response is submitted
    setTimeout(() => {
      const content = document.querySelector('.catchup-card-content');
      if (content) {
        content.scrollTop = content.scrollHeight;
      }
    }, 100);
  };

  const renderAskMedia = (attachment) => {
    if (attachment?.s3_key.startsWith('images' || 'image')) {
      return (
        <IonImg
          className="ask-media-border-radius"
          style={styles.imgStyle}
          src={attachment?.uri}
        />
      );
    }
    if (attachment?.s3_key.startsWith('videos')) {
      const videos = attachment?.transcoded_videos.length > 0 ? attachment?.transcoded_videos : [{ uri: attachment?.uri }];
      return (
        <VideoPlayer videos={videos} styleOverride={styles.videoContainerStyle} />
      );
    }
    if (attachment?.s3_key.startsWith('pdfs')) {
      return (
        <PdfIndicator attachment={attachment} overrideStyle={{ margin: '.5rem 0 0 0' }} />
      );
    }
    return null;
  };

  const handleEditClick = () => {
    setShowCelebration(false);
    setIsResponding(true);
  };

  return (
    <Box className="catchup-card-container">
      <Stack className="catchup-card-author-container" spacing={2} flexDirection="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <AvatarRole
              user={post.author}
              size="medium"
              unsetTopMargin
              overrideStyle={styles.avatarRoleStyles}
            />
          </Box>
          <Stack sx={styles.authorStackStyle}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={styles.postAuthorNameStyle}
            >
              {displayName(post?.author?.first_name, post?.author?.last_name)}
            </Typography>
            {affiliationName && (
              <Typography
                sx={styles.affiliationNameStyle}
                className="catchup-card-affiliation-name"
              >
                {affiliationName}
              </Typography>
            )}
          </Stack>
        </Stack>

        {(groupName || panelName) && (
          <Stack
            sx={styles.pillContainerStyle}
            className="catchup-card-pill-container"
          >
            <PostIndicator type="catchup_network" text={groupName || panelName} />
          </Stack>
        )}
      </Stack>

      <Box className="catchup-card-content">
        <Box sx={{ padding: '0 20px' }}>
          <Typography
            sx={{
              display: 'inline',
              fontSize: '24px',
              color: text.lightBlack,
              fontWeight: 400,
            }}
          >
            <span style={{ fontWeight: 700 }}>{CATCHUP_STRINGS.ask}</span>
            {' '}
            <TruncateText text={post.body} maxChars={truncateLength} />
          </Typography>

          {postAttachment && (
            <div style={styles.mediaContainerStyle}>
              {renderAskMedia(postAttachment)}
            </div>
          )}

          {showCelebration && (
            <>
              <Box className="celebration-response">
                <Stack flexDirection="row" alignItems="flex-start" gap={1} sx={{ width: '95%' }}>
                  <AvatarRole
                    user={user}
                    size="small"
                    unsetTopMargin
                  />
                  <Stack>
                    <Typography sx={{ color: text.lightBlack }}>
                      {submittedResponse?.body}
                    </Typography>
                    {submittedResponse?.attachments?.length > 0 && (
                      <div style={styles.mediaContainerStyle}>
                        {renderAskMedia(submittedResponse?.attachments[0])}
                      </div>
                    )}
                  </Stack>
                </Stack>
                <IonIcon
                  style={{ width: '24px', height: '24px', cursor: 'pointer' }}
                  src="/assets/icon/catchup/edit-icon-beige.svg"
                  onClick={handleEditClick}
                />
              </Box>

              <Stack flexDirection="column" alignItems="center" sx={{ paddingBottom: '1rem' }}>
                <Box className="celebration-animation">
                  <img
                    src="/assets/icon/catchup/celebration-confetti.svg"
                    alt=""
                    className="celebration-confetti"
                  />
                  <img src="/assets/icon/catchup/celebration-party-cone.svg" alt="celebration" />
                </Box>
                <Typography sx={styles.celebrationTextStyle}>
                  {CATCHUP_STRINGS.mensch}
                </Typography>
              </Stack>
            </>
          )}
        </Box>

        {!showCelebration && (
          !isResponding ? (
            <Box className="response-trigger" onClick={() => setIsResponding(true)}>
              <Typography sx={{ fontSize: '20px' }} color="text.secondary">
                {CATCHUP_STRINGS.addResponse}
              </Typography>
              <IonIcon
                style={styles.plusIconStyle}
                src="/assets/icon/orange-circle-white-plus.svg"
              />
            </Box>
          ) : (
            <ResponseBox
              post={post}
              onClose={() => setIsResponding(false)}
              onSubmit={handleSubmit}
              media={media}
              setMedia={setMedia}
              initialResponse={submittedResponse}
            />
          )
        )}
      </Box>
    </Box>
  );
};

export default CatchupCard;
